import { useMemo, useRef, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle, Plus, ChevronRight, BedDouble, Pencil, Printer } from 'lucide-react'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { BackLink, useBackNav, useBackNavLinkBuilder } from '@/components/navigation/BackLink'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { proceduresApi } from '@/api/proceduresApi'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { templateUniqueId } from '@/lib/versioning'
import { usePatient } from '@/hooks/usePatients'
import { PatientVisitsDialog } from '@/components/patient/PatientVisitsDialog'
import { PatientInformationCard } from '@/components/patient/PatientInformationCard'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { formatSex, isPatientNotFound, patientInitials, patientName } from '@/lib/patient'
import { procedureStatusMeta } from '@/lib/procedureStatus'
import { LisReviewDeepLink } from '@/components/lis-review/LisReviewDeepLink'
import type { PatientProcedureWithProgress } from '@/api/patientsApi'
import { usePatientRecord } from '@/hooks/usePatientRecord'
import { usePatientProducts } from '@/hooks/useProducts'
import { usePatientLisResults } from '@/hooks/useLisOrders'
import { formatLongDate, formatScheduled, HeaderFact, INK, MUTED, RecordButton, TonePill } from '@/components/record/RecordParts'
import {
  AtAGlance,
  ClinicalCard,
  dayZeroText,
  DonorsSection,
  HistoryTimeline,
  IdentityCard,
  LisSection,
  productsByRun,
  ProductsSection,
  RunProducts,
  runDonor,
  SectionNav,
  StorageSection,
} from '@/components/patient/record/PatientRecordSections'

function ProgressBar({ progress }: { progress: PatientProcedureWithProgress['progress'] }) {
  if (!progress || progress.total === 0) {
    return <span className='text-xs text-muted-foreground'>No steps</span>
  }
  const pct = Math.round((progress.completed / progress.total) * 100)
  return (
    <div className='flex items-center gap-2'>
      <div className='h-1.5 w-20 overflow-hidden rounded-full bg-slate-200'>
        <div className='h-full rounded-full bg-emerald-500' style={{ width: `${pct}%` }} />
      </div>
      <span className='whitespace-nowrap text-[13px] tabular-nums text-[#475467]'>
        {progress.completed} / {progress.total} steps
      </span>
    </div>
  )
}

// Per-user column layout for this patient's procedures. Procedure is locked
// (it identifies the row) and so is the chevron that opens it. See
// src/components/table/index.ts for the pattern.
const JOURNEY_PROCEDURES_TABLE_ID = 'patient-journey-procedures'

const JOURNEY_PROCEDURES_COLUMNS: TableColumnDef[] = [
  { id: 'procedure', header: 'Procedure', locked: true, sortable: true },
  // Type, donor and products (z8nrz7g57g): which runs used which bags.
  { id: 'type', header: 'Type', sortable: true },
  { id: 'donor', header: 'Donor' },
  { id: 'products', header: 'Products (DIN)' },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'progress', header: 'Progress', sortable: true },
  { id: 'started', header: 'Date / time', sortable: true },
  { id: 'createdBy', header: 'Created by', sortable: true },
  { id: 'open', header: 'Open', locked: true },
]

// Header sorting (z8nrz7e4h6). Unsorted, procedures keep the API's order.
const JOURNEY_PROCEDURES_SORT: SortAccessors<PatientProcedureWithProgress> = {
  procedure: p => p.procedureTemplate.name,
  type: p => p.procedureTemplate.type ?? null,
  status: p => procedureStatusMeta(p).label,
  // No forms to count shows no bar, so it sorts with the blanks.
  progress: p => (p.progress && p.progress.total > 0 ? p.progress.completed / p.progress.total : null),
  started: p => p.startedAt,
  createdBy: p => p.createdBy,
}

export function PatientJourneyPage() {
  const cols = useTableColumns(JOURNEY_PROCEDURES_TABLE_ID, JOURNEY_PROCEDURES_COLUMNS)
  const { mrn = '' } = useParams<{ mrn: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: patient, isPending, error } = usePatient(mrn)
  // The full record (z8nrz7g57g): clinical facts, donors and history from the
  // patient's forms; their products; every laboratory result.
  const record = usePatientRecord(mrn)
  const patientProducts = usePatientProducts(mrn)
  const lis = usePatientLisResults(mrn)
  const products = useMemo(() => patientProducts.data ?? [], [patientProducts.data])
  const runProducts = useMemo(() => productsByRun(products), [products])
  // Starting a procedure is a creation surface, so it offers one row per family
  // — the current ACTIVE version. The key carries 'offerable' because the
  // unfiltered list is cached under ['procedure-templates', 'list'] elsewhere
  // (PatientsOverviewPage's filter needs retired versions), and the two must
  // not share an entry.
  const { data: procedureTemplates = [] } = useQuery({
    queryKey: ['procedure-templates', 'list', 'offerable'],
    queryFn: () => procedureTemplatesApi.list({ offerable: true }),
  })

  const [visitsOpen, setVisitsOpen] = useState(false)
  // Edit opens the demographics card in edit mode; it closes again when the
  // correction is saved or cancelled.
  const [editingDemographics, setEditingDemographics] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const { can } = usePermission()
  const { user } = useAuth()
  const canEdit = can(PERMISSIONS.patientsEdit)

  const [selectedProcedureTemplateId, setSelectedProcedureTemplateId] = useState('')
  const [showStartForm, setShowStartForm] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const backTo = useBackNav()

  const start = useMutation({
    mutationFn: () =>
      proceduresApi.create({ patientMrn: mrn, procedureTemplateId: selectedProcedureTemplateId }),
    onSuccess: created => {
      void qc.invalidateQueries({ queryKey: ['patients'] })
      openProcedure(created.id)
    },
  })

  const handleStartProcedure = () => {
    if (!selectedProcedureTemplateId) {
      setFormError('Please select a procedure template before starting.')
      return
    }
    setFormError(null)
    start.mutate()
  }

  const selectedProcedureTemplate = procedureTemplates.find(p => p.id === selectedProcedureTemplateId)
  const procedures = useSortedRows(patient?.procedures, cols.sort, JOURNEY_PROCEDURES_SORT)
  const procedureName = (id: string) => patient?.procedures.find((p) => p.id === id)?.procedureTemplate.name ?? null
  const clinical = record.data?.clinical
  const recipientKind = [clinical?.transplant_type?.value, clinical?.planned_donor_type?.value].join(' ')
  const weight = clinical?.recipient_weight_kg ?? clinical?.last_weight_kg
  // An MRN with no Patient row is not an error: a procedure can still be opened for it,
  // and the row materialises when that happens. Deep links into /patients/:mrn have
  // to keep working for MRNs that only ever existed on submissions or procedures,
  // so this reads as an empty state rather than a red failure banner.
  const notFound = isPatientNotFound(error)
  const displayError = formError ?? start.error ?? (notFound ? null : error)
  const crumbLabel = (patient ? patientName(patient) : null) ?? 'Patient'
  const fullName = (patient ? patientName(patient) : null) ?? mrn
  const initials = patientInitials(patient ? patientName(patient) : null, mrn)

  // Carries THIS page — including its own `from` — so back from a procedure
  // reaches this journey, and back again reaches the list that sent us here
  // (z8nrz7czj8).
  const withOrigin = useBackNavLinkBuilder(crumbLabel)
  const openProcedure = (id: string) =>
    navigate(withOrigin(`/patients/${encodeURIComponent(mrn)}/procedures/${id}`))

  const facts: { label: string; value: string | null | undefined }[] = [
    { label: 'DOB', value: formatLongDate(patient?.birthDate) },
    { label: 'Age', value: patient?.age != null ? `${patient.age} yrs` : null },
    { label: 'Sex', value: formatSex(patient?.sex) },
    { label: 'ABO / Rh', value: clinical?.recipient_abo_rh?.value },
    { label: 'Weight', value: weight ? `${weight.value} kg` : null },
    { label: 'Diagnosis', value: clinical?.diagnosis?.value },
    { label: 'Day 0', value: dayZeroText(clinical?.planned_infusion_date) },
  ]

  return (
    <div className={cn('min-h-full bg-[#F4F6F9]', INK)}>
      {/* A LIS review notification whose result has no procedure lands here. */}
      <LisReviewDeepLink />

      {/* The patient header (z8nrz7g57g): who, the facts a coordinator checks
          first, and the page's actions. It scrolls away; the section bar below
          stays, and carries the name once this is gone. */}
      <section ref={headerRef} className='border-b border-[#E4E7EC] bg-white px-6 pb-5 pt-3'>
        <div className='mx-auto max-w-[1392px]'>
          <div className='mb-3 min-w-0 print:hidden'>
            <BackLink />
            {/* Dropped while a back link is showing: its origin crumb would repeat
                the link's words. With no `from` the trail is exactly as it was
                (z8nrz7czj8). */}
            {!backTo && <Breadcrumbs items={[{ label: 'Patients', to: '/patients' }, { label: crumbLabel }]} />}
          </div>
          <div className='flex flex-wrap items-center justify-between gap-6'>
            <div className='flex items-center gap-4'>
              <div className='flex h-14 w-14 flex-none items-center justify-center rounded-full bg-[#1F7BC6] text-xl font-bold text-white'>
                {initials}
              </div>
              <div className='flex flex-col gap-1'>
                <h1 className='m-0 text-[26px] font-bold leading-tight'>{fullName}</h1>
                <div className={cn('font-mono text-[12.5px]', MUTED)}>MRN {mrn}</div>
              </div>
            </div>
            <div className='flex flex-wrap gap-7'>
              {facts.filter((f) => f.value).map((f) => (
                <HeaderFact key={f.label} label={f.label} value={f.value} />
              ))}
            </div>
            <div className='flex flex-wrap items-center gap-2.5 print:hidden'>
              <TonePill tone='green'>Active patient</TonePill>
              {/autolog/i.test(recipientKind) ? (
                <TonePill tone='blue'>Autologous recipient</TonePill>
              ) : /allogen/i.test(recipientKind) ? (
                <TonePill tone='blue'>Allogeneic recipient</TonePill>
              ) : null}
              {/* Visits are reference material a coordinator checks and moves on
                  from, so they earn a button rather than permanent space. */}
              <RecordButton onClick={() => setVisitsOpen(true)}>
                <BedDouble className='h-4 w-4' />
                Patient visit history
              </RecordButton>
              {/* The summary prints from its own view: the app shell's scrolling pane
                  cuts a printout at the first screen. It opens in a new tab and prints. */}
              <RecordButton
                onClick={() => window.open(`/patients/${encodeURIComponent(mrn)}/print?auto=1`, '_blank', 'noopener')}
              >
                <Printer className='h-4 w-4' />
                Print summary
              </RecordButton>
              {patient && canEdit && (
                <RecordButton primary onClick={() => setEditingDemographics(true)} disabled={editingDemographics}>
                  <Pencil className='h-4 w-4' />
                  Edit
                </RecordButton>
              )}
            </div>
          </div>
        </div>
      </section>

      <SectionNav headerRef={headerRef} initials={initials} name={fullName} mrn={mrn} ready={!isPending} />

      <div className='mx-auto flex max-w-[1392px] flex-col gap-6 p-6'>
        {displayError && (
          <div className='flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {displayError instanceof Error ? displayError.message : displayError}
          </div>
        )}

        {isPending ? (
          <div className='flex items-center justify-center py-24'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : (
          <>
            {notFound && (
              <div className='rounded-[10px] border border-dashed border-[#D0D5DD] bg-white px-5 py-4 text-sm text-[#475467]'>
                No patient record for <span className='font-mono'>{mrn}</span> yet. Starting a
                procedure below will create one.
              </div>
            )}

            <section id='overview' className='flex scroll-mt-20 flex-col gap-4'>
              {patient && editingDemographics && (
                <PatientInformationCard
                  patient={patient}
                  canEdit={canEdit}
                  signerName={user?.fullName || user?.email}
                  autoEdit
                  onDone={() => setEditingDemographics(false)}
                />
              )}
              <div className='grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]'>
                {!editingDemographics && <IdentityCard patient={patient} mrn={mrn} isError={!!error && !notFound} />}
                <ClinicalCard record={record.data} isError={record.isError} />
                <AtAGlance
                  products={patientProducts.isError ? undefined : products}
                  lis={lis.isError ? undefined : lis.data}
                  record={record.data}
                />
              </div>
            </section>

            <HistoryTimeline
              record={record.data}
              lis={lis.isError ? undefined : lis.data}
              lisPending={lis.isPending}
              lisError={lis.isError}
              isLoading={record.isPending}
              isError={record.isError}
            />

            {/* Procedures */}
            <section id='procedures' className='scroll-mt-20 overflow-hidden rounded-[10px] border border-[#E4E7EC] bg-white'>
              <div className='flex flex-wrap items-center justify-between gap-3 px-6 py-5'>
                <div className='flex flex-col gap-1'>
                  <h2 className='m-0 text-xl font-bold'>Transplants &amp; procedures</h2>
                  <p className={cn('m-0 text-[13px]', MUTED)}>
                    {procedures.length === 0
                      ? 'No procedures opened for this patient yet.'
                      : 'Date/time, donor and the products (DIN) each one used. Select a row to continue its form journey.'}
                  </p>
                </div>
                {procedureTemplates.length > 0 && !showStartForm && (
                  <RecordButton onClick={() => setShowStartForm(true)}>
                    <Plus className='h-4 w-4' />
                    Start new procedure
                  </RecordButton>
                )}
              </div>

              {procedures.length > 0 && (
                <div className='overflow-x-persistent'>
                  <table className='w-full min-w-[900px] text-[13.5px]'>
                    <thead className='bg-table-header text-left text-[12.5px] text-table-header-foreground'>
                      <tr>
                        {cols.visible.map(({ def }) => (
                          <ResizableTh
                            key={def.id}
                            columnId={def.id}
                            columns={cols}
                            label={def.header}
                            className={def.id === 'open' ? 'px-3.5 py-[11px]' : 'whitespace-nowrap px-3.5 py-[11px] font-semibold'}
                            resizable={def.id !== 'open'}
                          >
                            {def.id === 'open' ? null : def.header}
                          </ResizableTh>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {procedures.map((p, i) => {
                        const meta = procedureStatusMeta(p)
                        return (
                          <tr
                            key={p.id}
                            role='link'
                            tabIndex={0}
                            aria-label={`Open procedure ${p.procedureTemplate.name}`}
                            onClick={() => openProcedure(p.id)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                openProcedure(p.id)
                              }
                            }}
                            className={cn(
                              'cursor-pointer border-b border-[#E4E7EC] align-top transition-colors last:border-0 hover:bg-[#F4F6F9] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                              i % 2 === 1 && 'bg-[#FAFBFC]',
                            )}
                          >
                            <ColumnCells
                              columns={cols}
                              cells={{
                                procedure: <td className='px-3.5 py-[11px] font-semibold'>{p.procedureTemplate.name}</td>,
                                type: <td className='px-3.5 py-[11px]'>{p.procedureTemplate.type ?? '—'}</td>,
                                donor: (
                                  <td className='px-3.5 py-[11px]'>
                                    {runDonor(record.data?.donors ?? [], p.id) ?? <span className='text-[#667085]'>—</span>}
                                  </td>
                                ),
                                products: (
                                  <td className='px-3.5 py-[11px]'>
                                    <RunProducts products={runProducts.get(p.id)} />
                                  </td>
                                ),
                                status: (
                                  <td className='px-3.5 py-[11px]'>
                                    <span
                                      className={cn(
                                        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold',
                                        meta.badge,
                                      )}
                                    >
                                      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
                                      {meta.label}
                                    </span>
                                  </td>
                                ),
                                progress: (
                                  <td className='px-3.5 py-[11px]'>
                                    <ProgressBar progress={p.progress} />
                                  </td>
                                ),
                                started: (
                                  <td className='whitespace-nowrap px-3.5 py-[11px]'>
                                    {new Date(p.startedAt).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}
                                    {p.scheduledDate && (
                                      <span className={cn('block text-xs', MUTED)}>
                                        Scheduled {formatScheduled(p.scheduledDate)}
                                      </span>
                                    )}
                                  </td>
                                ),
                                createdBy: <td className={cn('px-3.5 py-[11px]', MUTED)}>{p.createdBy}</td>,
                                open: (
                                  <td className='px-3.5 py-[11px]'>
                                    <ChevronRight className='h-4 w-4 text-muted-foreground/40' />
                                  </td>
                                ),
                              }}
                            />
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Start new — the only <select> on this page (an e2e smoke test
                  asserts a single, unambiguous one). */}
              {procedureTemplates.length === 0 ? (
                <p className='m-0 border-t border-[#E4E7EC] px-6 py-4 text-sm text-amber-700'>
                  No procedure templates configured yet. An admin must create forms and a
                  procedure template first.
                </p>
              ) : (
                showStartForm && (
                  <div className='border-t border-[#E4E7EC] px-6 py-4'>
                    <div className='max-w-xl space-y-4'>
                      <div>
                        <label className='mb-1.5 block text-xs font-medium text-muted-foreground'>
                          Procedure Template <span className='text-destructive'>*</span>
                        </label>
                        <select
                          value={selectedProcedureTemplateId}
                          onChange={e => {
                            setSelectedProcedureTemplateId(e.target.value)
                            setFormError(null)
                          }}
                          className='w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm'
                        >
                          <option value=''>Select a procedure template</option>
                          {procedureTemplates.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} · {templateUniqueId(p)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedProcedureTemplate && (
                        <div className='rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2 text-sm'>
                          <p className='font-medium text-blue-900'>{selectedProcedureTemplate.name}</p>
                          {selectedProcedureTemplate.description && (
                            <p className='mt-0.5 text-xs text-blue-800/80'>
                              {selectedProcedureTemplate.description}
                            </p>
                          )}
                          <p className='mt-1 text-xs text-blue-700'>
                            Forms will follow the sequence defined for this procedure template.
                          </p>
                        </div>
                      )}

                      <div className='flex gap-2'>
                        <Button
                          onClick={handleStartProcedure}
                          disabled={start.isPending || !selectedProcedureTemplateId}
                        >
                          {start.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                          ) : (
                            <Plus className='mr-2 h-4 w-4' />
                          )}
                          Confirm &amp; start
                        </Button>
                        <Button
                          variant='ghost'
                          onClick={() => {
                            setShowStartForm(false)
                            setSelectedProcedureTemplateId('')
                            setFormError(null)
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              )}
            </section>

            <ProductsSection
              products={products}
              isLoading={patientProducts.isPending}
              isError={patientProducts.isError}
              donors={record.data?.donors ?? []}
            />
            <LisSection results={lis.data} isLoading={lis.isPending} isError={lis.isError} />
            <DonorsSection
              record={record.data}
              isLoading={record.isPending}
              isError={record.isError}
              procedureName={procedureName}
              products={products}
              patient={{
                name: patient ? patientName(patient) : null,
                mrn,
                age: patient?.age,
                sex: formatSex(patient?.sex),
                abo: clinical?.recipient_abo_rh?.value ?? null,
                weight: weight ? `${weight.value} kg` : null,
              }}
            />
            <StorageSection products={products} record={record.data} isError={patientProducts.isError} />
          </>
        )}
      </div>

      <PatientVisitsDialog open={visitsOpen} onOpenChange={setVisitsOpen} mrn={mrn} />
    </div>
  )
}
